import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Badge, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import * as postapi from "../../api/postapi";

const STEP = {
  SELECT_ADDRESS: 1,
  PREVIEW: 2,
  RESULT: 3,
};

const ShipOrderWithPostexModal = ({ show, onHide, order, toggle }) => {
  const [step, setStep] = useState(STEP.SELECT_ADDRESS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Step 1
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressCode, setSelectedAddressCode] = useState("");

  // Step 2
  const [preview, setPreview] = useState(null);
  const [cities, setCities] = useState([]);
  const [cityName, setCityName] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [invoicePayment, setInvoicePayment] = useState("");
  const [items, setItems] = useState("");
  const [orderDetail, setOrderDetail] = useState("");
  const [transactionNotes, setTransactionNotes] = useState("");
  const [sendSms, setSendSms] = useState(true);
  const [isEditingDetails, setIsEditingDetails] = useState(false);

  // Step 3
  const [result, setResult] = useState(null);
  const [airwayBillLoading, setAirwayBillLoading] = useState(false);

  const resetState = () => {
    setStep(STEP.SELECT_ADDRESS);
    setIsLoading(false);
    setError("");
    setAddresses([]);
    setSelectedAddressCode("");
    setPreview(null);
    setCities([]);
    setCityName("");
    setOrderNumber("");
    setCustomerName("");
    setCustomerPhone("");
    setDeliveryAddress("");
    setInvoicePayment("");
    setItems("");
    setOrderDetail("");
    setTransactionNotes("");
    setSendSms(true);
    setIsEditingDetails(false);
    setResult(null);
    setAirwayBillLoading(false);
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  useEffect(() => {
    if (show && order) {
      setIsLoading(true);
      setError("");
      postapi
        .getOrderPostexMerchantAddresses()
        .then((res) => {
          setAddresses(res.data?.data || []);
          setIsLoading(false);
        })
        .catch((err) => {
          setError(err?.response?.data?.error?.message || "Failed to load pickup addresses.");
          setIsLoading(false);
        });
    }
  }, [show, order]);

  const handleNext = async () => {
    if (!selectedAddressCode) {
      setError("Please select a pickup address.");
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      const [previewRes, citiesRes] = await Promise.allSettled([postapi.getOrderPostexPreview(order.id, selectedAddressCode), postapi.getOrderPostexCities()]);

      if (previewRes.status === "rejected") {
        throw new Error(previewRes.reason?.response?.data?.error?.message || "Failed to load order preview.");
      }

      const data = previewRes.value.data?.data;
      const allCities = citiesRes.status === "fulfilled" ? citiesRes.value.data?.data || [] : [];
      const cityList = allCities.filter((c) => c.isDeliveryCity !== false);

      setCities(cityList);
      setPreview(data);
      setOrderNumber(data.order_number || "");
      setCustomerName(data.customerName || "");
      setCustomerPhone(data.customerPhone || "");
      setDeliveryAddress(data.deliveryAddress || "");
      setInvoicePayment(String(data.invoicePayment ?? ""));
      setItems(String(data.items ?? ""));
      setOrderDetail(data.orderDetail || "");

      const resolution = data?.cityResolution;
      if (resolution?.ok && resolution.cityName) {
        setCityName(resolution.cityName);
      } else if (resolution?.candidates?.length > 0) {
        setCityName(resolution.candidates[0].city || "");
      } else {
        setCityName("");
      }

      setStep(STEP.PREVIEW);
    } catch (err) {
      setError(err?.message || "Failed to load order preview.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!cityName) {
      setError("Please select a delivery city.");
      return;
    }
    if (!customerPhone.trim()) {
      setError("Customer phone is required.");
      return;
    }
    if (!invoicePayment || isNaN(Number(invoicePayment)) || Number(invoicePayment) < 0) {
      setError("Please enter a valid COD amount.");
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      const payload = {
        id: order.id,
        status: "Picked",
        delivery_partner_label: "PostEx",
        cityName,
        pickupAddressCode: selectedAddressCode,
        storeAddressCode: "",
        transactionNotes: transactionNotes.trim(),
        send_sms: sendSms,
        orderNumber: orderNumber.trim(),
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        deliveryAddress: deliveryAddress.trim(),
        invoicePayment: Number(invoicePayment),
        items: items.trim(),
        orderDetail: orderDetail.trim(),
      };
      const res = await postapi.changeOrderStatus(payload);
      setResult(res.data?.data);
      setStep(STEP.RESULT);
      toggle();
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Failed to ship order with PostEx.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadAirwayBill = async () => {
    try {
      setAirwayBillLoading(true);
      const res = await postapi.getOrderPostexAirwayBill(result.trackingNumber);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `airway-bill-${result.trackingNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download airway bill.");
    } finally {
      setAirwayBillLoading(false);
    }
  };

  const resolution = preview?.cityResolution;

  const renderCityDropdown = () => {
    if (cities.length > 0) {
      return (
        <Form.Select value={cityName} onChange={(e) => setCityName(e.target.value)}>
          <option value="">— Select city —</option>
          {cities.map((c) => {
            const name = typeof c === "string" ? c : c.operationalCityName || c.cityName;
            return (
              <option key={name} value={name}>
                {name}
              </option>
            );
          })}
        </Form.Select>
      );
    }
    if (resolution?.candidates?.length > 0) {
      return (
        <Form.Select value={cityName} onChange={(e) => setCityName(e.target.value)}>
          <option value="">— Select city —</option>
          {resolution.candidates.map((c) => (
            <option key={c.city} value={c.city}>
              {c.city} (score: {c.score})
            </option>
          ))}
        </Form.Select>
      );
    }
    return <Form.Control type="text" value={cityName} onChange={(e) => setCityName(e.target.value)} placeholder="Enter PostEx operational city" />;
  };

  const renderStep1 = () => (
    <>
      <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
        Select the pickup address for PostEx to collect the order from.
      </p>
      {isLoading ? (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        <>
          <Form.Label className="fw-semibold mb-2">
            Pickup Address <span className="text-danger">*</span>
          </Form.Label>
          <div className="d-flex flex-column gap-2">
            {addresses.map((addr) => {
              const isSelected = selectedAddressCode === addr.addressCode;
              return (
                <div
                  key={addr.addressCode}
                  onClick={() => {
                    setSelectedAddressCode(addr.addressCode);
                    setError("");
                  }}
                  style={{
                    cursor: "pointer",
                    border: `2px solid ${isSelected ? "#0d6efd" : "#dee2e6"}`,
                    borderRadius: "8px",
                    padding: "10px 14px",
                    backgroundColor: isSelected ? "#f0f6ff" : "#fff",
                    fontSize: "13px",
                    transition: "border-color 0.15s, background-color 0.15s",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-semibold mb-1" style={{ fontSize: "14px" }}>
                        {addr.contactPersonName}
                        {addr.cityName && (
                          <span className="ms-2 text-muted fw-normal" style={{ fontSize: "12px" }}>
                            {addr.cityName}
                          </span>
                        )}
                      </div>
                      <div className="text-muted">{addr.address}</div>
                      {(addr.phone1 || addr.phone2) && (
                        <div className="text-muted mt-1">
                          📞 {addr.phone1}
                          {addr.phone2 ? `, ${addr.phone2}` : ""}
                        </div>
                      )}
                    </div>
                    <div className="ms-3 mt-1">
                      <div
                        style={{
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          border: `2px solid ${isSelected ? "#0d6efd" : "#adb5bd"}`,
                          backgroundColor: isSelected ? "#0d6efd" : "transparent",
                          flexShrink: 0,
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {!!error && !selectedAddressCode && (
            <div className="text-danger mt-1" style={{ fontSize: "13px" }}>
              {error}
            </div>
          )}
        </>
      )}
    </>
  );

  const renderStep2 = () => (
    <>
      {preview && (
        <>
          <div className="mb-3 p-3 border rounded" style={{ backgroundColor: "#f8f9fa", fontSize: "13px" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0 fw-semibold">Order Details</h6>
              {!isEditingDetails ? (
                <Button size="sm" variant="link" className="p-0 text-secondary" style={{ fontSize: "12px" }} onClick={() => setIsEditingDetails(true)}>
                  <FontAwesomeIcon icon={faPencil} className="me-1" />
                  Edit
                </Button>
              ) : (
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0 text-danger"
                    style={{ fontSize: "12px" }}
                    onClick={() => {
                      setOrderNumber(preview.order_number || "");
                      setCustomerName(preview.customerName || "");
                      setCustomerPhone(preview.customerPhone || "");
                      setDeliveryAddress(preview.deliveryAddress || "");
                      setInvoicePayment(String(preview.invoicePayment ?? ""));
                      setItems(String(preview.items ?? ""));
                      setOrderDetail(preview.orderDetail || "");
                      setIsEditingDetails(false);
                    }}
                  >
                    <FontAwesomeIcon icon={faXmark} className="me-1" />
                    Cancel
                  </Button>
                  <Button size="sm" variant="link" className="p-0 text-success" style={{ fontSize: "12px" }} onClick={() => setIsEditingDetails(false)}>
                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                    Done
                  </Button>
                </div>
              )}
            </div>

            <div className="d-flex justify-content-between align-items-center border-bottom py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                Order #
              </span>
              {isEditingDetails ? <Form.Control size="sm" type="text" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Order number" /> : <span className="fw-medium">{orderNumber}</span>}
            </div>

            <div className="d-flex justify-content-between align-items-center border-bottom py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                Customer
              </span>
              {isEditingDetails ? <Form.Control size="sm" type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name" /> : <span>{customerName}</span>}
            </div>

            <div className="d-flex justify-content-between align-items-center border-bottom py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                Phone <span className="text-danger">*</span>
              </span>
              {isEditingDetails ? <Form.Control size="sm" type="text" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="e.g. 03001234567" /> : <span>{customerPhone}</span>}
            </div>

            <div className="d-flex justify-content-between align-items-center border-bottom py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                Address
              </span>
              {isEditingDetails ? (
                <Form.Control size="sm" as="textarea" rows={2} value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} placeholder="Delivery address" />
              ) : (
                <span className="text-end" style={{ maxWidth: "60%" }}>
                  {deliveryAddress}
                </span>
              )}
            </div>

            <div className="d-flex justify-content-between align-items-center border-bottom py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                COD (Rs.) <span className="text-danger">*</span>
              </span>
              {isEditingDetails ? (
                <InputGroup size="sm">
                  <InputGroup.Text>Rs.</InputGroup.Text>
                  <Form.Control type="number" min="0" value={invoicePayment} onChange={(e) => setInvoicePayment(e.target.value)} placeholder="e.g. 5500" />
                </InputGroup>
              ) : (
                <span className="fw-medium">Rs. {Number(invoicePayment).toLocaleString()}</span>
              )}
            </div>

            <div className="d-flex justify-content-between align-items-center border-bottom py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                Items
              </span>
              {isEditingDetails ? <Form.Control size="sm" type="number" min="1" value={items} onChange={(e) => setItems(e.target.value)} placeholder="No. of items" /> : <span>{items}</span>}
            </div>
            <div className="d-flex justify-content-between align-items-center py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                Item Detail
              </span>
              {isEditingDetails ? (
                <Form.Control size="sm" as="textarea" rows={2} value={orderDetail} onChange={(e) => setOrderDetail(e.target.value)} placeholder="Item description" />
              ) : (
                <span className="text-end" style={{ maxWidth: "60%" }}>
                  {orderDetail}
                </span>
              )}
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>
              Delivery City <span className="text-danger">*</span>
              {resolution?.ok && (
                <Badge bg="success" className="ms-2" style={{ fontSize: "11px" }}>
                  Auto-detected
                </Badge>
              )}
              {!resolution?.ok && (
                <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: "11px" }}>
                  Please confirm
                </Badge>
              )}
            </Form.Label>
            {renderCityDropdown()}
            {resolution && !resolution.ok && <Form.Text className="text-warning">{resolution.reason || "Could not auto-detect city from address."}</Form.Text>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Transaction Notes</Form.Label>
            <Form.Control as="textarea" rows={2} value={transactionNotes} onChange={(e) => setTransactionNotes(e.target.value)} placeholder="Optional notes for PostEx" />
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Check type="switch" id="postex-send-sms-toggle" label="Send SMS to customer with tracking number" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} />
          </Form.Group>
        </>
      )}
    </>
  );

  const renderStep3 = () => (
    <div className="text-center py-2">
      <div className="mb-3">
        <span style={{ fontSize: "48px" }}>✅</span>
      </div>
      <h5 className="fw-semibold text-success mb-1">Order Picked Successfully!</h5>
      <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
        The order has been created on PostEx and status updated to <strong>Picked</strong>.
      </p>
      <div className="p-3 border rounded mb-3" style={{ backgroundColor: "#f8f9fa", fontSize: "13px" }}>
        <div className="d-flex justify-content-between border-bottom py-1">
          <span className="text-muted">Tracking Number</span>
          <span className="fw-bold text-primary">{result?.trackingNumber}</span>
        </div>
        <div className="d-flex justify-content-between border-bottom py-1">
          <span className="text-muted">PostEx Status</span>
          <span>{result?.orderStatus}</span>
        </div>
        <div className="d-flex justify-content-between py-1">
          <span className="text-muted">Order Date</span>
          <span>{result?.orderDate}</span>
        </div>
      </div>
      <Button variant="outline-primary" size="sm" onClick={handleDownloadAirwayBill} disabled={airwayBillLoading}>
        {airwayBillLoading ? <Spinner animation="border" size="sm" /> : "Download Airway Bill (PDF)"}
      </Button>
    </div>
  );

  const getTitle = () => {
    if (step === STEP.SELECT_ADDRESS) return "Ship With PostEx — Select Pickup Address";
    if (step === STEP.PREVIEW) return "Ship With PostEx — Preview & Confirm";
    return "Ship With PostEx — Done";
  };

  return (
    <Modal show={show} onHide={handleClose} size="md" centered backdrop="static" aria-labelledby="postex-modal-title">
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="postex-modal-title" style={{ fontSize: "16px" }}>
          {getTitle()}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 pb-2">
        {error && (
          <div className="text-danger text-center mb-3 rounded-1 w-100" style={{ fontSize: "13px" }}>
            * {error}
          </div>
        )}
        {step === STEP.SELECT_ADDRESS && renderStep1()}
        {step === STEP.PREVIEW && renderStep2()}
        {step === STEP.RESULT && renderStep3()}
      </Modal.Body>
      <Modal.Footer className="border-0 px-4 pb-4 pt-2">
        {step === STEP.SELECT_ADDRESS && (
          <div className="d-flex gap-3 w-100">
            <span className="w-100 d-flex justify-content-center align-items-center text-secondary p-2" style={{ cursor: "pointer", border: "2px solid rgba(151, 156, 162, 1)", borderRadius: "6px" }} onClick={handleClose}>
              Cancel
            </span>
            <Button className="w-100" onClick={handleNext} disabled={isLoading || !selectedAddressCode}>
              {isLoading ? <Spinner animation="border" size="sm" /> : "Next"}
            </Button>
          </div>
        )}
        {step === STEP.PREVIEW && (
          <div className="d-flex gap-3 w-100">
            <span
              className="w-100 d-flex justify-content-center align-items-center text-secondary p-2"
              style={{ cursor: "pointer", border: "2px solid rgba(151, 156, 162, 1)", borderRadius: "6px" }}
              onClick={() => {
                setStep(STEP.SELECT_ADDRESS);
                setError("");
              }}
            >
              Back
            </span>
            <Button className="w-100" onClick={handleConfirm} disabled={isLoading || !cityName.trim()}>
              {isLoading ? <Spinner animation="border" size="sm" /> : "Confirm & Pick"}
            </Button>
          </div>
        )}
        {step === STEP.RESULT && (
          <Button className="w-100" onClick={handleClose}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ShipOrderWithPostexModal;
